"use client";

import { create } from "zustand";

import { isActiveFilterValue } from "../helpers/pendingFilters.helper";
import type { Facet, PendingFilters } from "../types/filters.type";

interface FiltersState {
  draftFacets: Facet[] | null;
  draftTotal: number | null;
  pendingFilters: PendingFilters | null;
  isLiveUpdateLoading: boolean;
  initDraft: (facets: Facet[], total: number) => void;
  updateDraft: (facets: Facet[], total: number) => void;
  discardDraft: () => void;
  initPendingFilters: (urlParams: PendingFilters) => void;
  updatePendingFilter: <K extends keyof PendingFilters>(
    field: K,
    value: PendingFilters[K],
  ) => void;
  removePendingFilter: (field: string, value: string) => void;
  clearPendingFilters: () => void;
  isPendingFilterActive: (field: string) => boolean;
  isOptionSelected: (field: string, value: string) => boolean;
}

export const useFiltersStore = create<FiltersState>((set, get) => ({
  draftFacets: null,
  draftTotal: null,
  pendingFilters: null,
  isLiveUpdateLoading: false,

  initDraft: (facets, total) =>
    set({ draftFacets: structuredClone(facets), draftTotal: total }),

  updateDraft: (facets, total) =>
    set({ draftFacets: facets, draftTotal: total, isLiveUpdateLoading: false }),

  discardDraft: () =>
    set({
      draftFacets: null,
      draftTotal: null,
      pendingFilters: null,
      isLiveUpdateLoading: false,
    }),

  initPendingFilters: (urlParams) => set({ pendingFilters: { ...urlParams } }),

  updatePendingFilter: (field, value) =>
    set((state) => ({
      pendingFilters: { ...state.pendingFilters, [field]: value },
    })),

  removePendingFilter: (field, value) =>
    set((state) => {
      const next = { ...(state.pendingFilters as Record<string, unknown>) };
      const current = next[field];
      if (!Array.isArray(current)) {
        delete next[field];
        return { pendingFilters: next as PendingFilters };
      }
      const updated = (current as string[]).filter((v) => v !== value);
      if (updated.length > 0) {
        next[field] = updated;
      } else {
        delete next[field];
      }
      return { pendingFilters: next as PendingFilters };
    }),

  clearPendingFilters: () => set({ pendingFilters: {} }),

  isPendingFilterActive: (field) => {
    const val = (get().pendingFilters as Record<string, unknown>)?.[field];
    return isActiveFilterValue(val);
  },

  isOptionSelected: (field, value) => {
    const val = (get().pendingFilters as Record<string, unknown>)?.[field];
    if (!Array.isArray(val)) return false;
    return (val as string[]).includes(value);
  },
}));
